import { useMemo } from "react";
import { dateTimeRecordToLocalisedDateTime } from "@visioni/schedules";
import {
  formattedCameraArmedState,
  getCurrentCameraArmedState,
  getTimezoneFromAlarmDetail,
} from "@src/features/alarms/utils/alarm-utils";
import { findNextTransitionOptimized } from "@src/features/alarms/utils/alarm-actions";
import { isArmedFromArmingType } from "@src/features/schedules/utils/schedule-utils";
import { AlarmConfiguration } from "@visioni/a3s-alarm-schedules/schema";

interface UseCameraAlarmPresentationOptions {
  alarmDetail?: AlarmConfiguration | null;
  currentTime: Date;
}

export function useCameraAlarmPresentation({ alarmDetail, currentTime }: UseCameraAlarmPresentationOptions) {
  const isAlarmConfigured = useMemo(() => {
    return !!alarmDetail?.settings;
  }, [alarmDetail]);

  const armedState = useMemo(() => {
    if (!alarmDetail) return null;
    return getCurrentCameraArmedState(alarmDetail, currentTime);
  }, [alarmDetail, currentTime]);

  const isArmed = useMemo(() => {
    if (!armedState) return false;
    return isArmedFromArmingType(armedState.armingType);
  }, [armedState]);

  const statusText = useMemo(() => {
    if (!alarmDetail) return "Loading...";
    if (!isAlarmConfigured) return "Not configured";
    if (!armedState) return "Disarmed";
    return formattedCameraArmedState(armedState);
  }, [alarmDetail, isAlarmConfigured, armedState]);

  const scheduleTime = useMemo(() => {
    if (!alarmDetail) return "";
    if (!isAlarmConfigured) return "No upcoming changes";

    const nextTransition = findNextTransitionOptimized({ alarmDetail });
    if (!nextTransition) return "No upcoming changes";

    const timezone = getTimezoneFromAlarmDetail(alarmDetail) || "UTC";
    const transitionDateTime = dateTimeRecordToLocalisedDateTime(nextTransition.datetimeRecord, timezone);

    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const day = dayNames[transitionDateTime.weekday - 1];

    const hours = transitionDateTime.hour;
    const minutes = transitionDateTime.minute;
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, "0");

    const action = nextTransition.toState === "armed" ? "Turns on" : "Turns off";
    return `${action} ${day} ${formattedHours}:${formattedMinutes} ${ampm}`;
  }, [alarmDetail, isAlarmConfigured]);

  return {
    armedState,
    isArmed,
    statusText,
    scheduleTime,
    isAlarmConfigured,
  };
}
